import type { NotificationChannelPrefs } from '@/types'

/** Backend `NotificationEvent` keys grouped for category-level UI. */
export const NOTIFICATION_CATEGORY_EVENTS: Record<string, string[]> = {
  jobs: [
    'job_assigned',
    'job_started_backoffice',
    'job_completed',
    'job_cancelled',
  ],
  quotes: [
    'quote_created',
    'quote_accepted',
    'quote_declined',
    'quote_expires',
  ],
  payments: [
    'invoice_created',
    'invoice_overdue',
    'invoice_overdue_client',
    'payment_overdue_internal',
    'payment_overdue_client',
    'payment_declined',
    'payment_received',
  ],
  contracts: ['contract_renewal'],
  tasks: ['task_created', 'task_assigned', 'task_due_reminder', 'task_overdue'],
}

export const DEFAULT_NOTIFICATION_CHANNEL_PREFS: NotificationChannelPrefs = {
  email: true,
  push: false,
  in_app: true,
}

export const DISABLED_NOTIFICATION_CHANNEL_PREFS: NotificationChannelPrefs = {
  email: false,
  push: false,
  in_app: true,
}

export const MANDATORY_NOTIFICATION_EVENTS = new Set<string>()

export function notificationPrefs(
  key: string,
  channels: Record<string, NotificationChannelPrefs>
): NotificationChannelPrefs {
  const prefs = channels[key] ?? DEFAULT_NOTIFICATION_CHANNEL_PREFS

  return {
    ...prefs,
    in_app: true,
  }
}

export function eventEnabled(prefs: NotificationChannelPrefs): boolean {
  return prefs.email
}

export function applyEventEnabled(
  channels: Record<string, NotificationChannelPrefs>,
  eventKey: string,
  enabled: boolean
): Record<string, NotificationChannelPrefs> {
  const current = channels[eventKey] ?? DEFAULT_NOTIFICATION_CHANNEL_PREFS

  return {
    ...channels,
    [eventKey]: {
      ...current,
      email: enabled,
      in_app: true,
    },
  }
}

export function withMandatoryEventChannels(
  channels: Record<string, NotificationChannelPrefs>
) {
  const next = { ...channels }
  for (const key of MANDATORY_NOTIFICATION_EVENTS) {
    next[key] = {
      ...(next[key] ?? DEFAULT_NOTIFICATION_CHANNEL_PREFS),
      email: true,
      in_app: true,
    }
  }
  return next
}

export function categoryChannelPrefs(
  categoryKey: string,
  channels: Record<string, NotificationChannelPrefs>
): NotificationChannelPrefs {
  const keys = NOTIFICATION_CATEGORY_EVENTS[categoryKey] ?? []
  const prefs = keys.map(k => notificationPrefs(k, channels))
  if (prefs.length === 0) return DEFAULT_NOTIFICATION_CHANNEL_PREFS

  return {
    email: prefs.every(p => p.email),
    push: prefs.every(p => p.push),
    in_app: prefs.every(p => p.in_app),
  }
}

export function applyCategoryChannel(
  categoryKey: string,
  channels: Record<string, NotificationChannelPrefs>,
  channel: keyof NotificationChannelPrefs,
  value: boolean
): Record<string, NotificationChannelPrefs> {
  const next = { ...channels }
  for (const eventKey of NOTIFICATION_CATEGORY_EVENTS[categoryKey] ?? []) {
    if (channel === 'in_app') {
      next[eventKey] = {
        ...(next[eventKey] ?? DEFAULT_NOTIFICATION_CHANNEL_PREFS),
        in_app: true,
      }
      continue
    }

    next[eventKey] = {
      ...(next[eventKey] ?? DEFAULT_NOTIFICATION_CHANNEL_PREFS),
      [channel]: value,
      in_app: true,
    }
  }
  return next
}

MANDATORY_NOTIFICATION_EVENTS.delete('invoice_created')
